/**
 * CalendarRoute — standalone page for the dedicated calendar route.
 * Renders CalendarView with isStandalone={true} (no ViewToggle, no Break Out button).
 * Wraps itself in ActiveBoardProvider to get board data from the URL param.
 *
 * Validates: Requirements 2.2, 2.4
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { ActiveBoardProvider, useActiveBoard } from '../state/active-board-context';
import { CalendarView } from '../components/board/CalendarView';

// ---------------------------------------------------------------------------
// Inner component — consumes ActiveBoardContext
// ---------------------------------------------------------------------------

function CalendarRouteContent({ boardId }: { readonly boardId: string }): React.ReactElement {
  const { state } = useActiveBoard();

  if (state.isLoading && !state.board) {
    return (
      <main aria-label="Calendar view" className="flex items-center justify-center h-screen">
        <p className="text-muted-foreground" role="status" aria-live="polite">Loading calendar...</p>
      </main>
    );
  }

  if (state.error && !state.board) {
    return (
      <main aria-label="Calendar view" className="flex flex-col items-center justify-center h-screen gap-4">
        <p className="text-destructive" role="alert">{state.error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
        >
          Retry
        </button>
      </main>
    );
  }

  return (
    <main aria-label="Calendar view" className="flex flex-col h-screen">
      <CalendarView
        cards={state.cards}
        lists={state.lists}
        boardId={boardId}
        isStandalone={true}
      />
    </main>
  );
}

// ---------------------------------------------------------------------------
// Page wrapper — reads boardId from URL, wraps in ActiveBoardProvider
// ---------------------------------------------------------------------------

export function CalendarRoute(): React.ReactElement {
  const { boardId } = useParams<{ boardId: string }>();

  if (!boardId) {
    return (
      <main aria-label="Calendar view">
        <p className="p-6 text-muted-foreground">No board selected.</p>
      </main>
    );
  }

  return (
    <ActiveBoardProvider boardId={boardId}>
      <CalendarRouteContent boardId={boardId} />
    </ActiveBoardProvider>
  );
}
